import { useEffect, useMemo, useState } from "react";
import useAvailibleMenu from "./useAvailibleMenu";
import { MenuTypeConst, type MenuType, type MenuRoute, type AvailibleMenuData } from "./menuTypes";
import type { LoginInfo } from "@/types/LoginInfo";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@hooks/useFeatures";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasPermission } from "@hooks/usePermissions";

/**
 * Return type of useSystemMenu hook
 */
interface SystemMenuData extends AvailibleMenuData {
  type: MenuType;
  loading: boolean;
  routes: MenuRoute[];
}

/**
 * Hook for managing the system menu routes
 *
 * @param loginInfo - Current user's login information including features and permissions
 * @returns Menu data including routes, available paths, and loading state
 */
const useSystemMenu = (_loginInfo: LoginInfo): SystemMenuData => {

  const routes = useMemo((): MenuRoute[] => {
    return [
      {
        collapse: true,
        name: translate(LanguageKey.trackers_and_sensors_key),
        state: "openTrackersSensors",
        icon: (
          <span className="tracker-profile-admin-menu-icon menu-icon"></span>
        ),
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_TRACKERS),
            to: "/trackers",
            layout: "",
            name: translate(LanguageKey.sidebar_admin_trackers_key),
            subPages: ["/import_tracker_settings"],
          },
          {
            view:
              hasFeature(FeatureKey.bulk_operations) &&
              hasPermission(UserPermission.MENU_ADMIN_BULK_OPERATION),
            to: "/bulk_manage",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_BULK_OPERATION_description_key),
            subPages: ["/bulk_operations", "/bulk_results_detail_system"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_TRACKER_PRODUCT),
            to: "/tracker_product",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_TRACKER_PRODUCT_description_key),
            subPages: ["/tracker_product_detail"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_BATTERY_CALCULATOR),
            to: "/battery_calculator",
            layout: "",
            name: translate(LanguageKey.battery_calc_battery_calculator_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_PRODUCT_AUTO_UPGRADE),
            to: "/product_auto_upgrade",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PRODUCT_AUTO_UPGRADE_description_key),
            subPages: ["/product_auto_upgrade_detail"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_PRODUCT_REGEXP_FILTER),
            to: "/product_regexp_filter",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PRODUCT_REGEXP_FILTER_description_key),
            subPages: ["/product_regexp_filter_detail"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_USAGE_PROFILE),
            to: "/device_usage_profile",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USAGE_PROFILE_description_key),
            subPages: ["/device_usage_profile_manage"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_BLE_SENSOR),
            to: "/ble_sensors",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_BLE_SENSOR_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.location_key),
        state: "openOrgSetting",
        icon: <span className="location-stop-icon menu-icon"></span>,
        views: [
          {
            view:
              hasFeature(FeatureKey.loc_beacon) &&
              hasPermission(UserPermission.MENU_ADMIN_GEOLAYER_GENERATOR),
            to: "/geolayer_generator",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOLAYER_GENERATOR_description_key),
            subPages: ["/geolayer_generator_manage"],
          },
          {
            view:
              hasFeature(FeatureKey.loc_beacon) &&
              hasPermission(UserPermission.MENU_ADMIN_INFRASTRUCTURE),
            to: "/infrastructures",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INFRASTRUCTURE_description_key),
            subPages: ["/import_infrastructure_settings"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_HARVESTED_CELLULAR),
            to: `/harvested_cellular`,
            layout: "",
            name: translate(LanguageKey.harvested_cell_towers_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_PRIVATE_CELLULAR),
            to: `/private_cellular_all`,
            layout: "",
            name: translate(LanguageKey.UserPermissionCategory_PRIVATE_CELLULAR_title_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_CELL_TOWER_TROUBLESHOOTING),
            to: `/wifi_cellular_mismatch`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_CELL_TOWER_TROUBLESHOOTING_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.organizations_key),
        state: "openOrgSetting",
        icon: <span className="org-admin-menu-icon menu-icon"></span>,
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_PARTNERS),
            to: "/partners",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PARTNERS_description_key),
            subPages: ["/partners_update", "/partners_create"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_LIST),
            to: `/organization`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_LIST_description_key),
          },
          {
            view:
              hasFeature(FeatureKey.demo_environment) && hasPermission(UserPermission.MENU_ADMIN_DEMO),
            to: "/org_demo",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_DEMO_description_key),
            subPages: ["/org_demo_manage"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_INTERNAL_PLATFORM_PLAN),
            to: `/internal_plans`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INTERNAL_PLATFORM_PLAN_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.access_management_key),
        state: "openParnerOrg",
        icon: <span className="access-icon menu-icon"></span>,
        views: [
          {
            view:
              hasFeature(FeatureKey.user_profile) &&
              hasPermission(UserPermission.MENU_ADMIN_USER_ROLE),
            to: "/role_manager",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USER_ROLE_description_key),
            subPages: ["/role_manager_update"],
          },
          {
            view: hasPermission(UserPermission.OAUTH2_CLIENT_VIEW),
            to: "/oauth2_registered_client",
            layout: "",
            name: translate(LanguageKey.oauth2_client_management_key),
            subPages: [
              "/oauth2_registered_client_update",
              "/oauth2_registered_client_create",
            ],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.organisation_billing_key),
        state: "openInsights",
        icon: <span className="invoicing-icon menu-icon"></span>,
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_INVOICING_OPERATIONS),
            to: "/invoicing_operations",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INVOICING_OPERATIONS_description_key),
            subPages: ["/invoicing_operations_detail"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_INVOICES),
            to: "/invoices",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INVOICES_description_key),
            subPages: ["/invoices_detail"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_RENEWAL_FORECASTS),
            to: "/quotation",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_RENEWAL_FORECASTS_description_key),
            subPages: ["/quotation_detail"],
          },
          {
            view:
              hasPermission(UserPermission.MENU_ADMIN_QUOTES) &&
              hasFeature(FeatureKey.quote_manage),
            to: "/quotes_storefront",
            layout: "",
            name: translate(LanguageKey.quotes_key),
            subPages: ["/quotes", "/quote_detail"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_PRICELIST),
            to: `/price_lists`,
            layout: "",
            name: translate(LanguageKey.price_list_key),
            subPages: ["/price_list_update", "/price_list_items"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_PRICE_BOOKS),
            to: `/pricebook`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PRICE_BOOKS_description_key),
            subPages: ["/pricebook_update", "/pricebook_increase"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_PRICE_BOOKS),
            to: `/customer`,
            layout: "",
            name: translate(LanguageKey.customers_key),
            subPages: ["/customer_detail"],
          },
          {
            view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            to: `/consolidated_customer`,
            layout: "",
            name: translate(LanguageKey.consolidated_customers_key),
            subPages: ["/consolidated_customer_update"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.salesforce_key),
        state: "openSalesforce",
        icon: <span className="salesforce-icon menu-icon"></span>,
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_ACCOUNT),
            to: "/salesforce_account",
            layout: "",
            name: translate(LanguageKey.accounts_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_CONTACT),
            to: "/salesforce_contact",
            layout: "",
            name: translate(LanguageKey.contacts_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_LEAD),
            to: "/salesforce_lead",
            layout: "",
            name: translate(LanguageKey.leads_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_OPPORTUNITY),
            to: "/salesforce_opportunity",
            layout: "",
            name: translate(LanguageKey.opportunities_key),
            subPages: ["/salesforce_opportunity_line_items", "/salesforce_opportunity_detail"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_QUOTE),
            to: "/salesforce_quote",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_SALESFORCE_QUOTE_description_key),
            subPages: ["/salesforce_quote_line_item", "/salesforce_quote_detail"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_CHANNEL_PARTNER),
            to: "/salesforce_channel_partner",
            layout: "",
            name: translate(LanguageKey.channel_partner_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_USER),
            to: "/salesforce_user",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USERS_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.impartner_key),
        state: "openImpartner",
        icon: <span className="partner-admin-menu-icon menu-icon"></span>,
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_IMPARTNER_ACCOUNT),
            to: "/impartner_account",
            layout: "",
            name: translate(LanguageKey.accounts_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_IMPARTNER_CHANNEL_PARTNER),
            to: "/impartner_channel_partner",
            layout: "",
            name: translate(LanguageKey.channel_partner_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_IMPARTNER_USER),
            to: "/impartner_user",
            layout: "",
            name: translate(LanguageKey.impartner_user_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.legal),
        state: "openLegal",
        icon: <span className="legal-icon menu-icon"></span>,
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_LEGAL_DOCUMENTS),
            to: "/legal_document",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_LEGAL_DOCUMENTS_description_key),
            subPages: [
              "/legal_document_import",
              "/legal_document_send",
              "/legal_document_view",
            ],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_LEGAL_TEMPLATE),
            to: "/legal_document_template",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_LEGAL_TEMPLATE_description_key),
            subPages: ["/legal_document_template_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.customization_key),
        state: "opencustomization",
        icon: <span className="ui-custom-icon menu-icon"></span>,
        views: [
          {
            view:
              hasFeature(FeatureKey.image_library) &&
              hasPermission(UserPermission.MENU_ADMIN_IMAGE_LIBRARY),
            to: "/image_library_all",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_IMAGE_LIBRARY_description_key),
            subPages: ["/image_library_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.sidebar_support_key),
        state: "openInsights",
        icon: <span className="support-icon menu-icon"></span>,
        views: [
          {
            view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            to: "/failed_trackers",
            layout: "",
            name: translate(LanguageKey.failed_trackers),
          },
          {
            view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            to: "/manufacturing",
            layout: "",
            name: translate(LanguageKey.v42_manufacturing_key),
            subPages: ["/manufacturing_order_detail"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_STOCK),
            to: "/stock",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_STOCK_description_key),
            subPages: ["/stock_update"],
          },
          {
            view:
              hasFeature(FeatureKey.stock_movement) &&
              hasPermission(UserPermission.MENU_STOCK_MOVEMENT),
            to: "/stock_movement",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_STOCK_MOVEMENT_description_key),
            subPages: ["/stock_movement_view"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_RMA_DECLARATION),
            to: `/rma_declaration`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_RMA_DECLARATION_description_key),
            subPages: ["/rma_declaration_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.communication_key),
        state: "openInsights",
        icon: <span className="communication-icon menu-icon"></span>,
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SIGFOX_ACCOUNT),
            to: "/sigfox_account",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_SIGFOX_ACCOUNT_description_key),
            subPages: [
              "/sigfox_account_update",
              "/sigfox_account_device_type_info",
            ],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_CELLULAR_ACCOUNT),
            to: "/cellular_account",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_CELLULAR_ACCOUNT_description_key),
            subPages: ["/cellular_account_manage"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_CELLULAR_SIM),
            to: "/cellular_sim",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_CELLULAR_SIM_description_key),
            subPages: ["/cellular_sim_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.platform_notifications_key),
        state: "openLocation",
        icon: <span className="notification-icon menu-icon"></span>,
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_NOTIFICATION),
            to: "/notification",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_NOTIFICATION_description_key),
            subPages: ["/notification_update"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_INTEGRATED_HELP),
            to: "/integrated_help",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INTEGRATED_HELP_description_key),
            subPages: ["/integrated_help_update"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.api_key),
        state: "openLocation",
        icon: <span className="api-icon menu-icon"></span>,
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_API_ACCESS),
            to: "/api-access",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_API_ACCESS_description_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_INTERNAL_API),
            to: "/internal-api",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INTERNAL_API_description_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_AI_PROMPTS),
            to: "/ai-prompts",
            layout: "",
            name: translate(LanguageKey.manage_ai_prompts_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_AI_CHATS),
            to: "/ai-chats",
            layout: "",
            name: translate(LanguageKey.manage_ai_chats_key),
            subPages: ["/ai-chat-view"],
          },
        ],
      },
    ];
  }, []);

  const [data, setData] = useState<SystemMenuData>({
    type: MenuTypeConst.ALL,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: "/organization",
  });

  const availibleMenuData = useAvailibleMenu(routes, "/organization");

  useEffect(() => {
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData]);

  return data;
};

export default useSystemMenu;
